export const CANDIDATE_BILLING_REQUIRED_FIELDS = [
  'legal_customer_name',
  'billing_email',
  'phone_number',
  'country',
] as const;

export type CandidateBillingRequiredField = typeof CANDIDATE_BILLING_REQUIRED_FIELDS[number];

export const CUSTOMER_SAFE_BILLING_CONFIRMATION_REASONS = [
  'confirmation_ack',
  'seek_profile',
  ...CANDIDATE_BILLING_REQUIRED_FIELDS,
] as const;

export type CustomerSafeBillingConfirmationReason = typeof CUSTOMER_SAFE_BILLING_CONFIRMATION_REASONS[number];

export interface CandidateBillingProfileLike {
  full_name?: unknown;
  email?: unknown;
  phone_number?: unknown;
  billing_country_code?: unknown;
}

const hasValue = (value: unknown): boolean => String(value ?? '').trim().length > 0;

export function missingCandidateBillingFields(
  profile: CandidateBillingProfileLike | null | undefined,
): CandidateBillingRequiredField[] {
  const candidate = profile ?? {};
  return CANDIDATE_BILLING_REQUIRED_FIELDS.filter((field) => {
    if (field === 'legal_customer_name') return !hasValue(candidate.full_name);
    if (field === 'billing_email') return !hasValue(candidate.email);
    if (field === 'phone_number') return !hasValue(candidate.phone_number);
    return !hasValue(candidate.billing_country_code);
  });
}

const isSafeReason = (value: unknown): value is CustomerSafeBillingConfirmationReason =>
  typeof value === 'string'
  && CUSTOMER_SAFE_BILLING_CONFIRMATION_REASONS.includes(value as CustomerSafeBillingConfirmationReason);

export function parseBillingConfirmationRequired(
  payload: unknown,
): CustomerSafeBillingConfirmationReason[] | null {
  const record = payload && typeof payload === 'object'
    ? payload as Record<string, unknown>
    : {};
  const rawCode = typeof record.code === 'string'
    ? record.code
    : typeof record.error === 'string'
      ? record.error
      : '';

  const isGovernedError = rawCode === 'BILLING_CONFIRMATION_REQUIRED'
    || rawCode.startsWith('BILLING_CONFIRMATION_REQUIRED:');
  if (!isGovernedError) return null;

  const supplied = Array.isArray(record.missing_fields)
    ? record.missing_fields
    : rawCode.includes(':')
      ? rawCode.slice(rawCode.indexOf(':') + 1).split(',')
      : [];
  const normalizedReasons = supplied
    .map((value) => typeof value === 'string' ? value.trim() : value);
  if (normalizedReasons.some((reason) => !isSafeReason(reason))) return null;
  const safeReasons = normalizedReasons.filter(isSafeReason);

  return [...new Set(safeReasons)];
}

export class BillingConfirmationRequiredError extends Error {
  readonly code = 'BILLING_CONFIRMATION_REQUIRED';

  constructor(readonly missingFields: CustomerSafeBillingConfirmationReason[]) {
    super('Billing confirmation is required before checkout.');
    this.name = 'BillingConfirmationRequiredError';
  }
}

export function billingConfirmationRequiredError(payload: unknown): BillingConfirmationRequiredError | null {
  const missingFields = parseBillingConfirmationRequired(payload);
  return missingFields === null ? null : new BillingConfirmationRequiredError(missingFields);
}

export function isBillingConfirmationRequiredError(
  error: unknown,
): error is BillingConfirmationRequiredError {
  return error instanceof BillingConfirmationRequiredError;
}
